import { Stack, styled } from '@mui/material'
import { Footer, tokens, TOOLBAR_HEIGHT } from 'ComponentLibrary'
import { ReactNode } from 'react'

const EXTRA_BOTTOM_SPACE = '120px'

type PageGridContainerProps = {
    children: ReactNode
    withBottomSpace?: boolean
    withTopSpace?: boolean // Add spacing at the top of the page before the headline. Should be omitted if a component is placed before the headline (e.g. breadcrumbs)
    withFooter?: boolean
    background?: string
}

export function PageGridContainer({ children, withBottomSpace, withTopSpace, background, withFooter }: PageGridContainerProps) {
    return (
        <Stack
            direction="column"
            width="100%"
            justifyContent="space-between"
            paddingTop={withTopSpace ? tokens.SizingSpacing400 : undefined}
            minHeight={withBottomSpace ? `calc(100vh - ${TOOLBAR_HEIGHT})` : undefined} // Always align footer at the end
            sx={{ background }}
        >
            <StyledPageContainer>
                <StyledPageGridContainer>{children}</StyledPageGridContainer>
            </StyledPageContainer>
            <Stack direction="row" height={withBottomSpace ? EXTRA_BOTTOM_SPACE : undefined} alignItems="flex-end">
                {withFooter && <Footer />}
            </Stack>
        </Stack>
    )
}

const StyledPageGridContainer = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    '@media (max-width: 1023px)': {
        gridTemplateColumns: 'repeat(4, 1fr)',
    },
    gap: tokens.SizingSpacing300,
})

const StyledPageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '1448px',
    width: '100%',
    padding: `0 ${tokens.SizingSpacing300}`,
    margin: '0 auto',
})

type PageGridItemProps = {
    children?: React.ReactNode
    rowSpan?: number
    rowSpanXs?: number
    columnSpan: 1 | 2 | 3 | 4 | 5 | 6
    columnSpanXs: 1 | 2 | 3 | 4
}

export function PageGridItem({ children, rowSpan = 1, rowSpanXs, columnSpan, columnSpanXs }: PageGridItemProps) {
    return (
        <StyledPageGridItem $rowSpan={rowSpan} $columnSpan={columnSpan} $columnSpanXs={columnSpanXs} $rowSpanXs={rowSpanXs}>
            {children}
        </StyledPageGridItem>
    )
}
const StyledPageGridItem = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $rowSpan: number; $rowSpanXs?: number; $columnSpan: number; $columnSpanXs: number }>(
    ({ $rowSpan, $rowSpanXs, $columnSpan, $columnSpanXs }) => ({
        gridColumn: `span ${$columnSpan}`,
        gridRow: `span ${$rowSpan}`,
        '@media (max-width: 1023px)': {
            gridRow: `span ${$rowSpanXs || $rowSpan}`,
            gridColumn: `span ${$columnSpanXs || ($columnSpan > 4 ? 4 : $columnSpan)}`,
        },
    })
)
